import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface UserRoleNameResult {
  roleName: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches the current user's role name from the roles table.
 * Uses the profile.role_id to look up the role name.
 * Returns null if user has no role assigned.
 */
export function useUserRoleName(): UserRoleNameResult {
  const { profile } = useAuth();
  const roleId = profile?.role_id;
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-role-name', roleId],
    queryFn: async (): Promise<string | null> => {
      if (!roleId) return null;
      
      const { data: role, error: fetchError } = await supabase
        .from('roles')
        .select('name')
        .eq('id', roleId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching role name:', fetchError);
        throw fetchError;
      }
      
      return role?.name || null;
    },
    enabled: !!roleId,
    staleTime: 10 * 60 * 1000, // 10 minutes - role names rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return {
    roleName: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}
